import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { wallpaperService, GenerationStatusResponse, QuotaStatus } from '../services/wallpaperService';
import { historyService } from '../services/historyService';
import Modal from './Modal';
import ImageCropper from './ImageCropper';

interface WallpaperScreenProps {
  isAuthenticated: boolean;
  onLogin: () => void;
}

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: calc(100vh - 200px);
  position: relative;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 10px;
  color: var(--color-black);
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin-bottom: 30px;
  color: var(--color-gray);
  text-align: center;
`;

const StepContainer = styled.div`
  background-color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 25px;
  margin-bottom: 30px;
  border-radius: 0;
`;

const StepTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-black);
  margin-bottom: 20px;
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: black;
  border-radius: 0;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  margin: 20px auto;
  width: 100%;
  max-width: 1000px;
`;

const Folder = styled.div`
  position: relative;
  animation: float 2.5s infinite ease-in-out;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  .top {
    background: linear-gradient(135deg, #ff9a56, #ff6f56);
    width: 100px;
    height: 25px;
    border-radius: 0;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    position: relative;
    z-index: 2;
  }
  
  .bottom {
    background: linear-gradient(135deg, #ffe563, #ffc663);
    width: 150px;
    height: 100px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-radius: 0;
    position: relative;
    top: -12px;
  }
  
  @keyframes float {
    0% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
    100% {
      transform: translateY(0px);
    }
  }
`;

const CustomFileUpload = styled.label`
  font-size: 1.3em;
  color: #000000;
  text-align: center;
  margin-top: 30px;
  padding: 20px 35px;
  background: white;
  border: 2px solid black;
  border-radius: 0;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: background 0.3s ease;
  display: inline-block;
  width: 280px;
  
  &:hover {
    background: #f0f0f0;
  }
  
  input[type="file"] {
    display: none;
  }
`;

const ImagePreview = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  border: 2px solid var(--color-black);
  border-radius: 0;
`;

const Button = styled.button<{ $primary?: boolean; $disabled?: boolean }>`
  background-color: ${props => props.$disabled ? 'var(--color-gray)' : 'var(--color-black)'};
  color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 14px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  margin: 10px 5px;
  border-radius: 0;
  
  &:hover:not(:disabled) {
    background-color: var(--color-white);
    color: var(--color-black);
    border-color: var(--color-black);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PromptInput = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-light-gray);
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  margin-bottom: 15px;

  &:focus {
    outline: none;
    border-color: var(--color-blue);
  }
`;

const PromptLabel = styled.label`
  display: block;
  font-size: 0.95rem;
  color: var(--color-gray);
  margin-bottom: 8px;
`;

const WaveMenu = styled.div`
  border: 4px solid black;
  border-radius: 0;
  width: 200px;
  height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  cursor: pointer;
  transition: ease 0.2s;
  position: relative;
  background: white;
  
  li {
    list-style: none;
    height: 30px;
    width: 4px;
    border-radius: 0;
    background: black;
    margin: 0 6px;
    padding: 0;
    animation-name: wave1;
    animation-duration: 0.3s;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    transition: ease 0.2s;
  }
  
  &:hover > li {
    background: white;
  }
  
  &:hover {
    background: black;
  }
  
  li:nth-child(2) {
    animation-name: wave2;
    animation-delay: 0.2s;
  }
  
  li:nth-child(3) {
    animation-name: wave3;
    animation-delay: 0.23s;
    animation-duration: 0.4s;
  }
  
  li:nth-child(4) {
    animation-name: wave4;
    animation-delay: 0.1s;
    animation-duration: 0.3s;
  }
  
  li:nth-child(5) {
    animation-delay: 0.5s;
  }
  
  li:nth-child(6) {
    animation-name: wave2;
    animation-duration: 0.5s;
  }
  
  li:nth-child(8) {
    animation-name: wave4;
    animation-delay: 0.4s;
    animation-duration: 0.25s;
  }
  
  li:nth-child(9) {
    animation-name: wave3;
    animation-delay: 0.15s;
  }
  
  @keyframes wave1 {
    from {
      transform: scaleY(1);
    }
    to {
      transform: scaleY(0.5);
    }
  }
  
  @keyframes wave2 {
    from {
      transform: scaleY(0.3);
    }
    to {
      transform: scaleY(0.6);
    }
  }
  
  @keyframes wave3 {
    from {
      transform: scaleY(0.6);
    }
    to {
      transform: scaleY(0.8);
    }
  }
  
  @keyframes wave4 {
    from {
      transform: scaleY(0.2);
    }
    to {
      transform: scaleY(0.5);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const ResultContainer = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 0;
  margin-top: 20px;
`;

const ResultTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-black);
  margin-bottom: 20px;
  text-align: center;
`;

const WallpaperGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
`;

const WallpaperCard = styled.div`
  position: relative;
  width: 480px;
  height: 480px;
  border-radius: 0;
  overflow: hidden;
  cursor: pointer;
  display: inline-block;
  margin: 10px;
`;

const WallpaperImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PreviewLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0;
  z-index: 10;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.05);
  }
`;

const WallpaperFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 0;
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
`;

const WallpaperMeta = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
`;

const DownloadButton = styled.button`
  background: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  border-radius: 0;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.6);
    transform: scale(1.1);
  }
`;

const PreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  cursor: pointer;
  overflow: hidden;
`;

const PreviewModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  cursor: default;
`;

const PreviewModalImage = styled.img<{ scale: number }>`
  max-width: 80vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 0;
  cursor: zoom-in;
  transition: transform 0.3s ease;
  transform: scale(${props => props.scale});
  
  &:hover {
    transform: scale(${props => props.scale * 1.05});
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
  border: none;
  border-radius: 0;
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const LoginModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoginModalContent = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 0;
  max-width: 400px;
  width: 90%;
  border: 2px solid var(--color-black);
`;

const LoginModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-black);
  margin-bottom: 15px;
  text-align: center;
`;

const LoginModalText = styled.p`
  font-size: 1rem;
  color: var(--color-gray);
  margin-bottom: 25px;
  text-align: center;
`;

const LoginModalButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const LoginModalButton = styled.button<{ $primary?: boolean }>`
  background-color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
  color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
    color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
    border-color: var(--color-black);
  }
`;

const QuotaModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const QuotaModalContent = styled.div`
  background-color: white;
  padding: 40px;
  border-radius: 0;
  max-width: 500px;
  width: 90%;
  border: 2px solid var(--color-black);
  text-align: center;
`;

const QuotaModalTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-black);
  margin-bottom: 20px;
`;

const QuotaModalText = styled.p`
  font-size: 1.1rem;
  color: var(--color-gray);
  margin-bottom: 30px;
  line-height: 1.6;
`;

const QuotaModalButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const QuotaModalButton = styled.button<{ $primary?: boolean }>`
  background-color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
  color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 0;
  transition: all 0.2s ease;
  min-width: 120px;
  
  &:hover {
    background-color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
    color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
    border-color: var(--color-black);
  }
`;

const SuccessModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const SuccessModalContent = styled.div`
  background-color: white;
  padding: 40px;
  border-radius: 0;
  max-width: 500px;
  width: 90%;
  border: 2px solid var(--color-black);
  text-align: center;
`;

const SuccessModalTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-black);
  margin-bottom: 20px;
`;

const SuccessModalText = styled.p`
  font-size: 1.1rem;
  color: var(--color-gray);
  margin-bottom: 20px;
  line-height: 1.6;
`;

const QuotaInfoBox = styled.div`
  background: var(--color-light-gray);
  border: 2px solid var(--color-black);
  padding: 20px;
  margin: 20px 0;
  text-align: center;
`;

const QuotaInfoTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-black);
  margin-bottom: 10px;
`;

const QuotaInfoText = styled.div`
  font-size: 0.95rem;
  color: var(--color-gray);
  line-height: 1.5;
`;

const SuccessModalButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const SuccessModalButton = styled.button<{ $primary?: boolean }>`
  background-color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
  color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 0;
  transition: all 0.2s ease;
  min-width: 120px;
  
  &:hover {
    background-color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
    color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
    border-color: var(--color-black);
  }
`;

const InfoBox = styled.div`
  background-color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 15px;
  margin: 15px 0;
  border-radius: 0;
`;

const InfoText = styled.p`
  font-size: 0.95rem;
  color: var(--color-gray);
  margin: 0;
  line-height: 1.6;
`;

const SizeSelectorContainer = styled.div`
  margin-bottom: 20px;
`;

const SizeSelectorLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-black);
  margin-bottom: 8px;
`;

const SizeSelector = styled.select`
  width: 100%;
  padding: 12px 16px;
  font-size: 1rem;
  border: 2px solid var(--color-black);
  background-color: var(--color-white);
  color: var(--color-black);
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;

  &:focus {
    outline: none;
    border-color: var(--color-blue);
  }

  &:hover {
    border-color: var(--color-blue);
  }
`;

const SizeOption = styled.option`
  background-color: var(--color-white);
  color: var(--color-black);
  padding: 8px;
`;

// Apple设备标准尺寸定义
const WALLPAPER_SIZES = {
  'phone': { width: 1170, height: 2532, label: 'Phone' },
  'tablet': { width: 2048, height: 2732, label: 'Tablet' },
  'desktop': { width: 2560, height: 1440, label: 'Laptop' }
};

const WallpaperScreen: React.FC<WallpaperScreenProps> = ({ isAuthenticated, onLogin }) => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedSize, setSelectedSize] = useState<keyof typeof WALLPAPER_SIZES>('phone');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState<GenerationStatusResponse | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageScale, setImageScale] = useState(1);
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const imageUrl = URL.createObjectURL(file);
        setSelectedImage(imageUrl);
        setShowCropper(true);
        setGenerationResult(null);
        setCroppedImageBlob(null);
      }
    },
    multiple: false
  });

  const handleCropComplete = (blob: Blob) => {
    setCroppedImageBlob(blob);
    setShowCropper(false);
    
    // 创建预览 URL
    const previewUrl = URL.createObjectURL(blob);
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
    }
    setSelectedImage(previewUrl);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
    }
    setSelectedImage(null);
  };

  const handleGenerateWallpaper = async () => {
    console.log('Generate button clicked!');
    console.log('Current states:', {
      croppedImageBlob: !!croppedImageBlob,
      isAuthenticated,
      selectedImage: !!selectedImage,
      showCropper,
      generationResult: !!generationResult
    });
    
    if (!croppedImageBlob) {
      console.log('No cropped image blob, showing notification');
      setNotificationMessage('Please upload and crop an image first');
      setShowNotificationModal(true);
      return;
    }

    if (!isAuthenticated) {
      console.log('Not authenticated, showing login modal');
      setShowLoginModal(true);
      return;
    }

    // 配额检查由服务器端处理，这里不需要预检查

    setIsGenerating(true);
    setGenerationResult(null);

    try {
      const sizeConfig = WALLPAPER_SIZES[selectedSize];
      console.log('Starting wallpaper generation...');
      console.log('   - Custom prompt:', customPrompt || 'None');
      console.log('   - Size:', sizeConfig.label, `(${sizeConfig.width}x${sizeConfig.height})`);
      console.log('   - Cropped image blob:', croppedImageBlob);
      
      const result = await wallpaperService.generateFromCropped(
        croppedImageBlob,
        customPrompt,
        sizeConfig.width,
        sizeConfig.height
      );
      
      console.log('Generation API call completed');

      console.log('Wallpaper generation successful:', result);
      console.log('Result wallpapers:', result.wallpapers);
      console.log('Result structure:', JSON.stringify(result, null, 2));
      setGenerationResult(result);
      
      // 壁纸已自动保存到数据库（后端处理）
      console.log('✅ Wallpaper automatically saved to database by backend');
      
      // 直接计算配额状态（基于当前状态 + 1）
      const currentQuota = quotaStatus || { used: 0, limit: 3 };
      const updatedQuota = {
        canGenerate: (currentQuota.used + 1) < currentQuota.limit,
        remaining: Math.max(0, currentQuota.limit - (currentQuota.used + 1)),
        used: currentQuota.used + 1,
        limit: currentQuota.limit
      };
      
      setQuotaStatus(updatedQuota);
      console.log('📊 Updated quota status for modal:', updatedQuota);
      
      // 显示成功模态框（包含配额信息）
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Generation failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      
      // 检查是否是配额超限错误
      if (error.response?.status === 429 && error.response?.data?.error?.code === 'DAILY_QUOTA_EXCEEDED') {
        console.log('Daily quota exceeded, showing quota modal');
        setShowQuotaModal(true);
      } else {
        setNotificationMessage(
          'Generation failed: ' + (error.response?.data?.error?.message || error.message || 'Please try again')
        );
        setShowNotificationModal(true);
      }
    } finally {
      console.log('Generation process finished, setting isGenerating to false');
      setIsGenerating(false);
    }
  };

  const handleDownload = (wallpaperUrl: string, filename: string = 'wallpaper.png') => {
    wallpaperService.downloadWallpaper(wallpaperUrl, filename);
  };

  const handleRecrop = () => {
    if (selectedImage) {
      setShowCropper(true);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageScale(prev => Math.max(0.5, Math.min(3, prev + delta)));
  };

  const handlePreviewClose = () => {
    setPreviewImage(null);
    setImageScale(1);
  };

  // 阻止预览模态框打开时的页面滚动
  useEffect(() => {
    if (previewImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // 清理函数
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [previewImage]);

  // 清理localStorage中的大数据
  useEffect(() => {
    historyService.cleanupStorage();
  }, []);

  // 加载配额状态
  const loadQuotaStatus = async () => {
    if (isAuthenticated) {
      try {
        const quota = await wallpaperService.getQuotaStatus();
        console.log('📊 Loaded quota status:', quota);
        setQuotaStatus(quota);
        return quota;
      } catch (error) {
        console.error('Failed to load quota status:', error);
        return null;
      }
    }
    return null;
  };

  // 当用户登录状态改变时加载配额状态
  useEffect(() => {
    loadQuotaStatus();
  }, [isAuthenticated]);

  if (isGenerating) {
    return (
      <LoadingOverlay>
        <WaveMenu>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
          <li></li>
        </WaveMenu>
      </LoadingOverlay>
    );
  }

  // 调试信息
  console.log('WallpaperScreen render states:', {
    croppedImageBlob: !!croppedImageBlob,
    selectedImage: !!selectedImage,
    showCropper,
    generationResult: !!generationResult,
    isGenerating
  });

  return (
    <Container>
      <Subtitle>
        Wallpaper generation powered by <span style={{ color: 'var(--color-blue)', fontWeight: '600' }}>Stability AI ControlNet</span>
      </Subtitle>

      {/* Upload Section */}
      {!croppedImageBlob && (
        <UploadContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <Folder>
            <div className="top"></div>
            <div className="bottom"></div>
          </Folder>
          <CustomFileUpload>
            Upload Photo
          </CustomFileUpload>
        </UploadContainer>
      )}

      {/* Cropped Image Preview and Actions */}
      {croppedImageBlob && !showCropper && selectedImage && !generationResult && (
        <StepContainer>
          <ImagePreview>
            <PreviewImage src={selectedImage} alt="Cropped pattern" />
            
            {/* Size Selector */}
            <SizeSelectorContainer>
              <SizeSelectorLabel htmlFor="wallpaper-size">
                Select Wallpaper Size
              </SizeSelectorLabel>
              <SizeSelector
                id="wallpaper-size"
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value as keyof typeof WALLPAPER_SIZES)}
              >
                {Object.entries(WALLPAPER_SIZES).map(([key, config]) => (
                  <SizeOption key={key} value={key}>
                    {config.label} ({config.width}×{config.height})
                  </SizeOption>
                ))}
              </SizeSelector>
            </SizeSelectorContainer>
            
            <ButtonGroup>
              <Button onClick={handleRecrop}>Recrop</Button>
              <Button onClick={handleGenerateWallpaper} $disabled={isGenerating} disabled={isGenerating}>
                {isGenerating ? 'Generating...' : 'Generate'}
              </Button>
            </ButtonGroup>
          </ImagePreview>
        </StepContainer>
      )}

      {/* Generation Result */}
      {generationResult && generationResult.wallpapers && generationResult.wallpapers.length > 0 && (
        <ResultContainer>
          <ResultTitle>Generation Complete</ResultTitle>
          
          <WallpaperGrid>
            {generationResult.wallpapers.map((wallpaper, index) => (
              <WallpaperCard key={wallpaper.id} onClick={() => setPreviewImage(wallpaper.url)}>
                <WallpaperImage src={wallpaper.url} alt={`Generated wallpaper ${index + 1}`} />
                <PreviewLabel>Preview</PreviewLabel>
                <WallpaperFooter>
                  <WallpaperMeta>
                    {wallpaper.metadata?.width || 1290}×{wallpaper.metadata?.height || 2796}
                  </WallpaperMeta>
                  <DownloadButton onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(wallpaper.url, `wallpaper-${Date.now()}.png`);
                  }}>
                    Download
                  </DownloadButton>
                </WallpaperFooter>
              </WallpaperCard>
            ))}
          </WallpaperGrid>
          
          <ButtonGroup style={{ marginTop: '30px' }}>
            <Button onClick={() => {
              setGenerationResult(null);
              setCroppedImageBlob(null);
              setSelectedImage(null);
            }}>
              Generate New Wallpaper
            </Button>
          </ButtonGroup>

        </ResultContainer>
      )}

      {/* 图片裁剪器 */}
      {showCropper && selectedImage && (
        <ImageCropper
          imageSrc={selectedImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* Preview Modal */}
      {previewImage && (
        <PreviewModal onClick={handlePreviewClose}>
          <PreviewModalContent onClick={(e) => e.stopPropagation()} onWheel={handleWheel}>
            <CloseButton onClick={handlePreviewClose}>
              Close
            </CloseButton>
            <PreviewModalImage 
              src={previewImage} 
              alt="Full preview" 
              scale={imageScale}
            />
          </PreviewModalContent>
        </PreviewModal>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModalOverlay onClick={() => setShowLoginModal(false)}>
          <LoginModalContent onClick={(e) => e.stopPropagation()}>
            <LoginModalTitle>Login Required</LoginModalTitle>
            <LoginModalText>
              Please log in to generate wallpapers. New users get 3 free daily generations!
            </LoginModalText>
            <LoginModalButtonContainer>
              <LoginModalButton $primary onClick={() => {
                setShowLoginModal(false);
                navigate('/login');
              }}>
                Go to Login
              </LoginModalButton>
              <LoginModalButton onClick={() => setShowLoginModal(false)}>
                Cancel
              </LoginModalButton>
            </LoginModalButtonContainer>
          </LoginModalContent>
        </LoginModalOverlay>
      )}

      {/* Notification Modal */}
      {showNotificationModal && (
        <Modal
          isOpen={showNotificationModal}
          onClose={() => setShowNotificationModal(false)}
          title="Notification"
        >
          <p>{notificationMessage}</p>
        </Modal>
      )}

      {/* Success Modal with Quota Info */}
      {showSuccessModal && (
        <SuccessModalOverlay onClick={() => setShowSuccessModal(false)}>
          <SuccessModalContent onClick={(e) => e.stopPropagation()}>
            <SuccessModalTitle>Wallpaper Generated Successfully!</SuccessModalTitle>
            <SuccessModalText>
              Your wallpaper has been generated and saved to your history.
            </SuccessModalText>
            
            <QuotaInfoBox>
              <QuotaInfoTitle>Daily Generation Limit</QuotaInfoTitle>
              <QuotaInfoText>
                {quotaStatus ? (
                  <>
                    You have used {quotaStatus.used} of {quotaStatus.limit} daily generations today.
                    <br />
                    {quotaStatus.remaining > 0 ? (
                      <>You have {quotaStatus.remaining} generations remaining today.</>
                    ) : (
                      <>You have reached your daily limit. Come back tomorrow for more free generations!</>
                    )}
                  </>
                ) : (
                  <>You have used 1 of 3 daily generations today. You have 2 generations remaining today.</>
                )}
                <br /><br />
                We're working on subscription plans for unlimited daily generations!
              </QuotaInfoText>
            </QuotaInfoBox>
            
            <SuccessModalButtonContainer>
              <SuccessModalButton $primary onClick={() => setShowSuccessModal(false)}>
                Continue
              </SuccessModalButton>
              <SuccessModalButton onClick={() => {
                setShowSuccessModal(false);
                navigate('/profile');
              }}>
                View Profile
              </SuccessModalButton>
            </SuccessModalButtonContainer>
          </SuccessModalContent>
        </SuccessModalOverlay>
      )}

      {/* Quota Exceeded Modal */}
      {showQuotaModal && (
        <QuotaModalOverlay onClick={() => setShowQuotaModal(false)}>
          <QuotaModalContent onClick={(e) => e.stopPropagation()}>
            <QuotaModalTitle>Daily Limit Reached</QuotaModalTitle>
            <QuotaModalText>
              You have used all 3 daily wallpaper generations. 
              <br /><br />
              We're currently working on subscription plans that will offer unlimited generations. 
              Please check back tomorrow for your free daily quota or stay tuned for our upcoming subscription options!
            </QuotaModalText>
            <QuotaModalButtonContainer>
              <QuotaModalButton $primary onClick={() => {
                setShowQuotaModal(false);
                navigate('/profile');
              }}>
                View Profile
              </QuotaModalButton>
              <QuotaModalButton onClick={() => setShowQuotaModal(false)}>
                Close
              </QuotaModalButton>
            </QuotaModalButtonContainer>
          </QuotaModalContent>
        </QuotaModalOverlay>
      )}
    </Container>
  );
};

export default WallpaperScreen;
